import type { IndexUiState } from 'instantsearch.js';
import { showExamBoard } from '../settings/search';
import {
  CONTEXT_QUERY_FILTERS,
  FILLER_WORDS,
  QUERY_FILTERS,
} from '../settings/searchQueryFilters';
import { SUGGESTION_ATTRIBUTES, SuggestionAttribute } from '../settings/searchSuggestions';
import { buildSearchPath } from './searchSuggestions';

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Lowercase single-spaced words, so aliases only need writing one way.
const toWords = (text: string) =>
  text
    .toLowerCase()
    .replace(/[-,]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

// Every alias across the filters, longest first, so "a level further" is tried before "a level".
const toAliases = (filters: Partial<Record<SuggestionAttribute, Record<string, string[]>>>) =>
  SUGGESTION_ATTRIBUTES.flatMap((attribute) =>
    Object.entries(filters[attribute] ?? {}).flatMap(([value, aliases]) =>
      aliases.map((alias) => ({
        attribute,
        value,
        alias,
        // Space-delimited rather than \b, so aliases like "11+" still match as a whole word.
        pattern: new RegExp(`(^| )${escapeRegExp(alias)}(?= |$)`),
      })),
    ),
  ).sort((a, b) => b.alias.length - a.alias.length);

const ALIASES = toAliases(QUERY_FILTERS);
const CONTEXT_ALIASES = toAliases(CONTEXT_QUERY_FILTERS);

export interface QueryFilters {
  query: string;
  refinementList: Partial<Record<SuggestionAttribute, string[]>>;
}

// Splits the filter words out of a typed search, e.g. "a level past papers edexcel 2019" gives
// the A Level (GCE), Past Papers and Edexcel filters plus the query "2019". Returns null when
// nothing matched.
export const parseQueryFilters = (query: string): QueryFilters | null => {
  const refinementList: QueryFilters['refinementList'] = {};
  let remaining = toWords(query)
    .filter((word) => !FILLER_WORDS.includes(word))
    .join(' ');

  const match = (aliases: typeof ALIASES) =>
    aliases.forEach(({ attribute, value, pattern }) => {
      // One value per filter; a second course or board stays in the query.
      if (refinementList[attribute] || !pattern.test(remaining)) {
        return;
      }

      refinementList[attribute] = [value];
      remaining = remaining.replace(pattern, '$1').replace(/\s+/g, ' ').trim();
    });

  match(ALIASES);

  if (Object.keys(refinementList).length) {
    match(CONTEXT_ALIASES);
  }

  return Object.keys(refinementList).length ? { query: remaining, refinementList } : null;
};

// As parseQueryFilters, for moving filter words into the search state (URL and dropdowns).
//
// Also returns null when the result would show no hits. The search page hides results until a
// course is chosen (and, for courses with boards, a board or resource type) unless there's a
// query, so "igcse maths" stays a text search rather than turning into an empty page asking
// for an exam board.
export const extractQueryFilters = (query: string): QueryFilters | null => {
  const filters = parseQueryFilters(query);

  if (!filters) {
    return null;
  }

  const { query: remaining, refinementList } = filters;
  const course = refinementList.doc_exam_course?.[0];
  const showsResults =
    Boolean(remaining) ||
    (Boolean(course) &&
      (!showExamBoard.includes(course as any) ||
        Boolean(refinementList.doc_exam_board || refinementList.doc_type)));

  return showsResults ? filters : null;
};

type FacetFilters = string | (string | string[])[];

const hasFacetFilter = (facetFilters: FacetFilters | undefined, attribute: string) =>
  [facetFilters ?? []].flat(2).some((filter) => filter.startsWith(`${attribute}:`));

// The filter words in a typed search that actually apply on top of the dropdowns. A filter
// already chosen in a dropdown wins: its words are still taken out of the query, but no second,
// conflicting filter is added.
export const appliedQueryFilters = (
  query: string,
  isChosen: (attribute: SuggestionAttribute) => boolean,
): QueryFilters | null => {
  const filters = parseQueryFilters(query);

  if (!filters) {
    return null;
  }

  const refinementList: QueryFilters['refinementList'] = {};
  SUGGESTION_ATTRIBUTES.forEach((attribute) => {
    if (filters.refinementList[attribute] && !isChosen(attribute)) {
      refinementList[attribute] = filters.refinementList[attribute];
    }
  });

  return { query: filters.query, refinementList };
};

// Rewrites one InstantSearch request so filter words in the query search as filters, e.g.
// "a level past papers" as the A Level (GCE) and Past Papers filters rather than text that
// also matches International A Level and AS Level. The search box and URL keep what was typed.
export const applyQueryFiltersToParams = <
  Params extends { query?: string; facetFilters?: FacetFilters },
>(
  params: Params,
): Params => {
  const filters = appliedQueryFilters(params.query ?? '', (attribute) =>
    hasFacetFilter(params.facetFilters, attribute),
  );

  if (!filters) {
    return params;
  }

  const added = SUGGESTION_ATTRIBUTES.filter(
    (attribute) => filters.refinementList[attribute],
  ).map((attribute) => [`${attribute}:${filters.refinementList[attribute]![0]}`]);

  return {
    ...params,
    query: filters.query,
    facetFilters: [...[params.facetFilters ?? []].flat(1), ...added],
  };
};

// The whole search state for a typed search, with any filter words turned into filters.
// Keys follow the order InstantSearch's router writes them (filters, then query), so the URL
// isn't immediately rewritten as a second history entry.
export const queryToIndexUiState = (term: string): IndexUiState => {
  const q = term.trim();
  const filters = extractQueryFilters(q);

  if (!filters) {
    return q ? { query: q } : {};
  }

  const refinementList: Record<string, string[]> = {};
  SUGGESTION_ATTRIBUTES.forEach((attribute) => {
    if (filters.refinementList[attribute]) {
      refinementList[attribute] = filters.refinementList[attribute];
    }
  });

  return filters.query ? { refinementList, query: filters.query } : { refinementList };
};

// Moves the filter words in a typed search into the dropdowns (the search page's Enter key).
// Unlike a header search, filters already chosen are kept, and win over typed ones, as
// dropdowns such as Module can be picked while the course is still only typed.
export const mergeQueryIntoIndexUiState = (
  previous: IndexUiState,
  term: string,
): IndexUiState => {
  const typed = queryToIndexUiState(term);
  const merged = { ...typed.refinementList, ...previous.refinementList };

  // Router order: the suggestion attributes are mounted first, then the dropdowns.
  const refinementList: Record<string, string[]> = {};
  [...SUGGESTION_ATTRIBUTES, ...Object.keys(merged)].forEach((attribute) => {
    if (merged[attribute]?.length) {
      refinementList[attribute] = merged[attribute];
    }
  });

  return {
    ...(previous.configure && { configure: previous.configure }),
    ...(Object.keys(refinementList).length && { refinementList }),
    ...(typed.query && { query: typed.query }),
  };
};

export const buildQuerySearchPath = (term: string): string =>
  buildSearchPath(queryToIndexUiState(term));
